import { FC } from 'react';
import { IoMdTrash } from 'react-icons/io';
import { useTranslation } from 'react-i18next';
import { ListConcentration } from '../../../../interfaces/list-concentration';
import ReactTooltip from 'react-tooltip';

/**
 * Props for the ConcentrationList component
 * @typedef {Object} ConcentrationListProps
 * @property {ListConcentration[]} concentrations - Array of concentration items to display
 * @property {Function} onDelete - Function to call when a concentration is deleted
 * @property {Function} onSelect - Function to call when a concentration is selected
 * @property {boolean} canManage - Whether the user has permission to manage concentrations
 * @property {ListConcentration|null} selectedConcentration - Currently selected concentration item
 */
type ConcentrationListProps = {
  concentrations: ListConcentration[];
  onDelete: Function;
  onSelect: Function;
  canManage: boolean;
  selectedConcentration: ListConcentration | null;
};

/**
 * @description Component for displaying a list of drug concentrations with selection and delete functionality.
 *
 * @SADtrace SAD-003-3.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {ConcentrationListProps} props - Component props
 *
 * @returns {JSX.Element} Rendered component
 */
const ConcentrationList: FC<ConcentrationListProps> = ({
  concentrations,
  onDelete,
  onSelect,
  canManage,
  selectedConcentration,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {concentrations.length === 0 && (
        <div data-testid="concentration-list-no-records" className="alert mb-0">
          {t('messages.no_records')}
        </div>
      )}

      {concentrations.length > 0 && (
        <div
          className="master-list"
          data-testid="concentration-list-master-list"
        >
          {concentrations.map((concentration: ListConcentration) => {
            return (
              <a
                aria-label="concentration-list-item"
                href="/#"
                onClick={(e) => {
                  e.preventDefault();
                  onSelect(concentration);
                }}
                className={`master-list__item ${
                  concentration.id === selectedConcentration?.id ? 'active' : ''
                }`}
                key={concentration.id}
              >
                {concentration.name}

                {canManage && (
                  <>
                    <button
                      aria-label="concentration-list-item-delete"
                      className="master-list__item-delete"
                      onClick={() => onDelete(concentration)}
                      data-tip
                      data-for="concentration-delete-tooltip"
                      data-testid={`concentration-list-item-delete-id_${concentration.id}`}
                    >
                      <IoMdTrash size={26} />
                    </button>
                    <ReactTooltip
                      id="concentration-delete-tooltip"
                      effect="solid"
                      place="top"
                    >
                      Delete Concentration
                    </ReactTooltip>
                  </>
                )}
              </a>
            );
          })}
        </div>
      )}
    </>
  );
};

export default ConcentrationList;
